/**
 * {@link ConflictResolution} — displays unresolved score conflicts for an event
 * with both score versions side by side and per-conflict resolution actions.
 *
 * Requirements: 21.2, 21.3, 21.4
 */

import { useState, useEffect, useCallback } from 'react';
import { Button, ErrorBanner, Spinner } from '@scoring/frontend-shared';
import type { ApiClient } from '@scoring/frontend-shared';
import type { CSSProperties } from 'react';

/** Shape of a score conflict returned by the API. */
interface ScoreConflictView {
  conflictId: string;
  participantName: string | null;
  stationNumber: number;
  acceptedScore: {
    hits: number;
    misses: number;
    isDns: boolean;
    recordedBy: string;
    recordedAt: string;
  };
  conflictingScore: {
    hits: number;
    misses: number;
    isDns: boolean;
    recordedBy: string;
    recordedAt: string;
  };
  status: 'unresolved' | 'resolved';
}

export interface ConflictResolutionProps {
  eventId: string;
  apiClient: ApiClient;
}

const containerStyle: CSSProperties = {
  padding: '0',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1rem',
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: '1.125rem',
  fontWeight: 600,
};

const emptyStyle: CSSProperties = {
  padding: '1.5rem',
  background: '#dafbe1',
  borderRadius: 'var(--radius-button, 8px)',
  color: 'var(--color-brand-green, #1B4332)',
  fontSize: 'var(--font-size-body-sm, 0.875rem)',
  textAlign: 'center',
};

const conflictCardStyle: CSSProperties = {
  border: '1px solid var(--color-border, #E5E7EB)',
  borderRadius: 'var(--radius-button, 8px)',
  marginBottom: '1rem',
  overflow: 'hidden',
};

const conflictHeaderStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0.75rem 1rem',
  background: 'var(--color-bg-page, #FAFBFC)',
  borderBottom: '1px solid var(--color-border, #E5E7EB)',
  fontSize: 'var(--font-size-body-sm, 0.875rem)',
  fontWeight: 600,
};

const conflictBodyStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '0',
};

const scoreColumnStyle: CSSProperties = {
  padding: '0.75rem 1rem',
  fontSize: 'var(--font-size-body-sm, 0.875rem)',
};

const scoreColumnLeftStyle: CSSProperties = {
  ...scoreColumnStyle,
  borderRight: '1px solid var(--color-border, #E5E7EB)',
};

const columnTitleStyle: CSSProperties = {
  fontWeight: 600,
  marginBottom: '0.5rem',
  color: 'var(--color-text-primary, #1A1A2E)',
};

const scoreDetailStyle: CSSProperties = {
  marginBottom: '0.25rem',
  color: 'var(--color-text-secondary, #6B7280)',
};

const actionsRowStyle: CSSProperties = {
  display: 'flex',
  gap: '0.5rem',
  padding: '0.75rem 1rem',
  borderTop: '1px solid var(--color-border, #E5E7EB)',
  background: 'var(--color-bg-page, #FAFBFC)',
};

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatScore(score: { hits: number; misses: number; isDns: boolean }): string {
  if (score.isDns) return 'DNS';
  return `${score.hits} hits / ${score.misses} misses`;
}

export function ConflictResolution({
  eventId,
  apiClient,
}: ConflictResolutionProps): JSX.Element {
  const [conflicts, setConflicts] = useState<ScoreConflictView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const fetchConflicts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.request<{ conflicts: ScoreConflictView[] }>(
        'GET',
        `/events/${encodeURIComponent(eventId)}/conflicts`,
      );
      setConflicts(data.conflicts.filter((c) => c.status === 'unresolved'));
      setError(null);
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to load conflicts.');
    } finally {
      setLoading(false);
    }
  }, [apiClient, eventId]);

  useEffect(() => {
    void fetchConflicts();
  }, [fetchConflicts]);

  async function handleResolve(conflictId: string, version: 'server' | 'device'): Promise<void> {
    setResolvingId(conflictId);
    setError(null);
    try {
      await apiClient.request<unknown>(
        'PUT',
        `/events/${encodeURIComponent(eventId)}/conflicts/${encodeURIComponent(conflictId)}/resolve`,
        { body: { acceptedVersion: version } },
      );
      // Remove the resolved conflict from the list
      setConflicts((prev) => prev.filter((c) => c.conflictId !== conflictId));
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to resolve conflict.');
    } finally {
      setResolvingId(null);
    }
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h3 style={titleStyle}>Score Conflicts</h3>
      </div>

      {error && (
        <ErrorBanner title="Error" onDismiss={() => setError(null)}>
          {error}
        </ErrorBanner>
      )}

      {loading ? (
        <Spinner label="Loading conflicts…" />
      ) : conflicts.length === 0 ? (
        <div style={emptyStyle}>
          No unresolved score conflicts for this event.
        </div>
      ) : (
        <div role="list" aria-label="Unresolved score conflicts">
          {conflicts.map((conflict) => (
            <div
              key={conflict.conflictId}
              role="listitem"
              style={conflictCardStyle}
              aria-label={`Conflict for ${conflict.participantName} at station ${String(conflict.stationNumber)}`}
            >
              {/* Conflict header — participant and station */}
              <div style={conflictHeaderStyle}>
                <span>{conflict.participantName ?? 'Unknown participant'} — Station {conflict.stationNumber}</span>
              </div>

              {/* Side-by-side score comparison */}
              <div style={conflictBodyStyle}>
                {/* Original (server) score */}
                <div style={scoreColumnLeftStyle}>
                  <div style={columnTitleStyle}>Original Score (Server)</div>
                  <div style={scoreDetailStyle}>
                    {formatScore(conflict.acceptedScore)}
                  </div>
                  <div style={scoreDetailStyle}>
                    Recorded by: {conflict.acceptedScore.recordedBy}
                  </div>
                  <div style={scoreDetailStyle}>
                    {formatTimestamp(conflict.acceptedScore.recordedAt)}
                  </div>
                </div>

                {/* Conflicting (device) score */}
                <div style={scoreColumnStyle}>
                  <div style={columnTitleStyle}>New Score (Device)</div>
                  <div style={scoreDetailStyle}>
                    {formatScore(conflict.conflictingScore)}
                  </div>
                  <div style={scoreDetailStyle}>
                    Recorded by: {conflict.conflictingScore.recordedBy}
                  </div>
                  <div style={scoreDetailStyle}>
                    {formatTimestamp(conflict.conflictingScore.recordedAt)}
                  </div>
                </div>
              </div>

              {/* Resolution actions */}
              <div style={actionsRowStyle}>
                <Button
                  variant="secondary"
                  onClick={() => { void handleResolve(conflict.conflictId, 'server'); }}
                  disabled={resolvingId === conflict.conflictId}
                  aria-label={`Keep original score for ${conflict.participantName} at station ${String(conflict.stationNumber)}`}
                >
                  {resolvingId === conflict.conflictId ? 'Resolving…' : 'Keep Original'}
                </Button>
                <Button
                  onClick={() => { void handleResolve(conflict.conflictId, 'device'); }}
                  disabled={resolvingId === conflict.conflictId}
                  aria-label={`Keep new score for ${conflict.participantName} at station ${String(conflict.stationNumber)}`}
                >
                  {resolvingId === conflict.conflictId ? 'Resolving…' : 'Keep New'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
